import { Document, DocumentFormData, DocumentFilter } from '../types';
import { documentCacheService, PaginatedCacheResult } from '@/services/document-cache-service';

// API base URL - should be configured based on environment
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api`;

// API token for production
const API_TOKEN = 'obta-production-api-token-2025';

// CACHE DISABLE FLAG - Set to true to disable cache system and fetch all documents in one shot
const DISABLE_CACHE_SYSTEM = true; // Change to false to re-enable cache system

// Enhanced pagination interface
export interface PaginatedDocuments {
  documents: Document[];
  total: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  fromCache: boolean;
  cacheStats?: any;
}

// Helper function to get the authentication token
// TODO: Implement proper token retrieval (e.g., from localStorage or auth context)

// Helper function to handle API errors
const handleApiError = (error: any): never => {
  console.error('API Error:', error);
  // Consider more user-friendly error handling or a global error handler
  throw new Error(error.message || 'An error occurred while communicating with the API');
};

// Helper function to convert various timestamp formats to Unix timestamp
const normalizeTimestamp = (timestamp: any): number => {
  if (!timestamp) {
    return Math.floor(Date.now() / 1000);
  }
  
  // If it's already a number (Unix timestamp), return it
  if (typeof timestamp === 'number') {
    // Check if it's in milliseconds (13 digits) or seconds (10 digits)
    return timestamp > 9999999999 ? Math.floor(timestamp / 1000) : timestamp;
  }
  
  // If it's a string, try to parse it as a date
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      // If parsing failed, return current timestamp
      return Math.floor(Date.now() / 1000);
    }
    return Math.floor(date.getTime() / 1000);
  }
  
  // Fallback to current timestamp
  return Math.floor(Date.now() / 1000);
};

// Define the DocumentHistory interface (adjust based on actual API response)
export interface DocumentHistoryEntry {
  id: string;
  timestamp: number;
  user: {
    id: string;
    username: string;
    full_name: string;
  };
  action: string;
  changes: string[]; // Or a more structured type for changes
  raw_details: {
    previous: any;
    current: any; // Or 'changes' depending on API
  };
}

export interface DocumentHistoryResponse {
  document_id: string;
  history: DocumentHistoryEntry[];
}

export const DocumentsService = {
  /**
   * Get documents for a specific page with caching support
   */
  async getDocumentsPage(
    page: number = 1, 
    pageSize: number = 20, 
    filters?: DocumentFilter
  ): Promise<PaginatedDocuments> {
    try {
      console.log(`🔍 Getting documents page ${page} (size: ${pageSize}) - Cache disabled: ${DISABLE_CACHE_SYSTEM}`);
      
      if (DISABLE_CACHE_SYSTEM) {
        // CACHE DISABLED: Fetch all documents in one shot and handle pagination client-side
        console.log('🚫 Cache system disabled - fetching all documents');
        
        const params = new URLSearchParams();
        params.append('limit', '1000'); // Fetch up to 1000 documents (should cover all)
        params.append('offset', '0');
        
        if (filters) {
          if (filters.document_type) {
            filters.document_type.forEach(dt => params.append('document_type', dt));
          }
          if (filters.visibility) {
            filters.visibility.forEach(v => params.append('visibility', v));
          }
          if (filters.date_range?.start) params.append('date_start', filters.date_range.start);
          if (filters.date_range?.end) params.append('date_end', filters.date_range.end);
          if (filters.categories) {
            filters.categories.forEach(c => params.append('category', c));
          }
          if (filters.tags) {
            filters.tags.forEach(t => params.append('tag', t));
          }
          if (filters.search_term) params.append('search_term', filters.search_term);
          if (filters.sort_by) params.append('sort_by', filters.sort_by);
          if (filters.sort_order) params.append('sort_order', filters.sort_order);
        }

        const response = await fetch(`${API_BASE_URL}/documents?${params.toString()}`, {
          headers: {
            'X-API-Token': API_TOKEN,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch documents: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const allDocuments = (data.results || data).map((doc: any) => this.transformDocument(doc));
        
        // Handle client-side pagination
        const total = allDocuments.length;
        const totalPages = Math.ceil(total / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const pageDocuments = allDocuments.slice(startIndex, endIndex);
        
        console.log(`📊 Fetched ${total} documents, showing ${pageDocuments.length} for page ${page}`);
        
        return {
          documents: pageDocuments,
          total,
          currentPage: page,
          totalPages,
          pageSize,
          fromCache: false,
          cacheStats: { totalDocuments: total, hitRate: 0, cacheHits: 0, cacheMisses: 0, lastUpdated: Date.now(), memoryUsage: 0 }
        };
      } else {
        // CACHE ENABLED: Use normal server-side pagination
        console.log('🌐 Fetching documents from API with server-side pagination');
        
        const params = new URLSearchParams();
        params.append('limit', pageSize.toString());
        params.append('offset', ((page - 1) * pageSize).toString());
        
        if (filters) {
          if (filters.document_type) {
            filters.document_type.forEach(dt => params.append('document_type', dt));
          }
          if (filters.visibility) {
            filters.visibility.forEach(v => params.append('visibility', v));
          }
          if (filters.date_range?.start) params.append('date_start', filters.date_range.start);
          if (filters.date_range?.end) params.append('date_end', filters.date_range.end);
          if (filters.categories) {
            filters.categories.forEach(c => params.append('category', c));
          }
          if (filters.tags) {
            filters.tags.forEach(t => params.append('tag', t));
          }
          if (filters.search_term) params.append('search_term', filters.search_term);
          if (filters.sort_by) params.append('sort_by', filters.sort_by);
          if (filters.sort_order) params.append('sort_order', filters.sort_order);
        }

        const response = await fetch(`${API_BASE_URL}/documents?${params.toString()}`, {
          headers: {
            'X-API-Token': API_TOKEN,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch documents: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('📊 API Response:', { 
          total: data.total, 
          resultCount: data.results?.length || data.length,
          pagination: data.pagination 
        });
        
        const documents = (data.results || data).map((doc: any) => this.transformDocument(doc));
        
        // Add to cache for future use (but don't rely on cache for pagination)
        if (documents.length > 0) {
          documentCacheService.addDocuments(documents);
        }
        
        // Calculate proper pagination info
        const total = data.total || 0;
        const totalPages = Math.ceil(total / pageSize);
        
        return {
          documents,
          total,
          currentPage: page,
          totalPages,
          pageSize,
          fromCache: false,
          cacheStats: documentCacheService.getStats()
        };
      }
    } catch (error) {
      console.error('Error in getDocumentsPage:', error);
      throw error;
    }
  },

  /**
   * Get total document count with caching
   */
  async getDocumentCount(filters?: DocumentFilter): Promise<number> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.document_type) {
          filters.document_type.forEach(dt => params.append('document_type', dt));
        }
        if (filters.visibility) {
          filters.visibility.forEach(v => params.append('visibility', v));
        }
        if (filters.date_range?.start) params.append('date_start', filters.date_range.start);
        if (filters.date_range?.end) params.append('date_end', filters.date_range.end);
        if (filters.categories) {
          filters.categories.forEach(c => params.append('category', c));
        }
        if (filters.tags) {
          filters.tags.forEach(t => params.append('tag', t));
        }
        if (filters.search_term) params.append('search_term', filters.search_term);
      }

      const response = await fetch(`${API_BASE_URL}/documents/count?${params.toString()}`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document count: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.total || 0;
    } catch (error) {
      console.error('Error getting document count:', error);
      throw error;
    }
  },

  /**
   * Warm the cache by loading all documents
   */
  async warmCache(): Promise<void> {
    try {
      console.log('🔥 Starting cache warming...');
      
      // First, get the total count
      const totalCount = await this.getDocumentCount();
      console.log(`📊 Total documents to cache: ${totalCount}`);
      
      if (totalCount === 0) {
        console.log('🤷 No documents found, cache warming complete');
        return;
      }
      
      // Calculate how many requests we need
      const batchSize = 100; // Fetch 100 documents at a time
      const totalBatches = Math.ceil(totalCount / batchSize);
      
      console.log(`📦 Will fetch ${totalBatches} batches of ${batchSize} documents each`);
      
      // Fetch all documents in batches
      for (let batch = 0; batch < totalBatches; batch++) {
        const offset = batch * batchSize;
        console.log(`📥 Fetching batch ${batch + 1}/${totalBatches} (offset: ${offset})`);
        
        const params = new URLSearchParams();
        params.append('limit', batchSize.toString());
        params.append('offset', offset.toString());

        const response = await fetch(`${API_BASE_URL}/documents?${params.toString()}`, {
          headers: {
            'X-API-Token': API_TOKEN,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.warn(`⚠️ Failed to fetch batch ${batch + 1}: ${response.status} ${response.statusText}`);
          continue;
        }

        const data = await response.json();
        const documents = (data.results || data).map((doc: any) => this.transformDocument(doc));
        
        // Add to cache
        documentCacheService.addDocuments(documents);
        
        // Small delay to prevent overwhelming the API
        if (batch < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      const stats = documentCacheService.getStats();
      console.log(`🎉 Cache warming complete! Cached ${stats.totalDocuments} documents`);
    } catch (error) {
      console.error('❌ Cache warming failed:', error);
      throw error;
    }
  },

  /**
   * Transform raw API document to our Document interface
   */
  transformDocument(doc: any): Document {
    return {
      ...doc,
      // Ensure audit object exists with the expected structure
      audit: {
        created_by: doc.created_by || 1,
        created_at: normalizeTimestamp(doc.created_at),
        updated_at: normalizeTimestamp(doc.updated_at || doc.created_at),
        published_at: doc.published_at ? normalizeTimestamp(doc.published_at) : null
      },
      // Ensure other required fields have defaults
      entities: doc.entities || [],
      tags: doc.tags || [],
      categories: doc.categories || [],
      ocr_text: doc.ocr_text || '',
      ocr_quality: doc.ocr_quality || 'basic',
      media: doc.media || {
        storage_path: '',
        thumbnail_path: doc.thumbnail_path || '',
        file_size: 0,
        file_type: 'application/pdf',
        color_mode: 'color'
      },
      preservation: doc.preservation || {
        source_archive: doc.source || 'Unknown',
        date_archived: new Date().toISOString(),
        condition: 'good'
      },
      relations: doc.relations || {
        related_articles: [],
        event_id: null,
        person_id: [],
        advertiser_id: null,
        collections: []
      },
      metadata: doc.metadata || {}
    };
  },

  /**
   * Update CRUD operations to automatically update cache
   */
  async updateDocument(id: string, documentData: Partial<DocumentFormData>): Promise<Document> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
        method: 'PATCH',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(documentData),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Update document error response:", errorBody);
        throw new Error(`Failed to update document: ${response.status} ${response.statusText} - ${errorBody}`);
      }

      const doc = await response.json();
      const transformedDoc = this.transformDocument(doc);
      
      // Update cache
      documentCacheService.updateDocument(transformedDoc);
      
      return transformedDoc;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete a document and update cache
   */
  async deleteDocument(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Token': API_TOKEN,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.status} ${response.statusText}`);
      }
      
      // Remove from cache
      documentCacheService.removeDocument(id);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Create a new document and update cache
   */
  async createDocument(documentData: DocumentFormData): Promise<Document> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents`, {
        method: 'POST',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(documentData),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Create document error response:", errorBody);
        throw new Error(`Failed to create document: ${response.status} ${response.statusText} - ${errorBody}`);
      }

      const doc = await response.json();
      const transformedDoc = this.transformDocument(doc);
      
      // Add to cache
      documentCacheService.addDocuments([transformedDoc]);
      
      return transformedDoc;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get all documents with optional filtering (legacy method for backward compatibility)
   */
  async getDocuments(filters?: DocumentFilter): Promise<Document[]> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        if (filters.document_type) {
          filters.document_type.forEach(dt => params.append('document_type', dt));
        }
        if (filters.visibility) {
          filters.visibility.forEach(v => params.append('visibility', v));
        }
        if (filters.date_range?.start) params.append('date_start', filters.date_range.start);
        if (filters.date_range?.end) params.append('date_end', filters.date_range.end);
        if (filters.categories) {
          filters.categories.forEach(c => params.append('category', c));
        }
        if (filters.tags) {
          filters.tags.forEach(t => params.append('tag', t));
        }
        if (filters.search_term) params.append('search_term', filters.search_term);
      }

      // Add a reasonable limit for better performance
      params.append('limit', '100'); // Fetch up to 100 documents for better performance
      params.append('offset', '0');

      const response = await fetch(`${API_BASE_URL}/documents?${params.toString()}`, {
        headers: {
          'X-API-Token': API_TOKEN, // Replace with Authorization: Bearer <token> if using JWT
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const documents = data.results || data;
      
      // Transform API response to match expected Document interface
      return documents.map((doc: any) => ({
        ...doc,
        // Ensure audit object exists with the expected structure
        audit: {
          created_by: doc.created_by || 1,
          created_at: normalizeTimestamp(doc.created_at),
          updated_at: normalizeTimestamp(doc.updated_at || doc.created_at),
          published_at: doc.published_at ? normalizeTimestamp(doc.published_at) : null
        },
        // Ensure other required fields have defaults
        entities: doc.entities || [],
        tags: doc.tags || [],
        categories: doc.categories || [],
        ocr_text: doc.ocr_text || '',
        ocr_quality: doc.ocr_quality || 'basic',
        media: doc.media || {
          storage_path: '',
          thumbnail_path: doc.thumbnail_path || '',
          file_size: 0,
          file_type: 'application/pdf',
          color_mode: 'color'
        },
        preservation: doc.preservation || {
          source_archive: doc.source || 'Unknown',
          date_archived: new Date().toISOString(),
          condition: 'good'
        },
        relations: doc.relations || {
          related_articles: [],
          event_id: null,
          person_id: [],
          advertiser_id: null,
          collections: []
        },
        metadata: doc.metadata || {}
      }));
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get a single document by ID
   */
  async getDocumentById(id: string): Promise<Document> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
      }

      const doc = await response.json();
      
      // Transform API response to match expected Document interface (same as getDocuments)
      return {
        ...doc,
        // Ensure audit object exists with the expected structure
        audit: {
          created_by: doc.created_by || 1,
          created_at: normalizeTimestamp(doc.created_at),
          updated_at: normalizeTimestamp(doc.updated_at || doc.created_at),
          published_at: doc.published_at ? normalizeTimestamp(doc.published_at) : null
        },
        // Ensure other required fields have defaults
        entities: doc.entities || [],
        tags: doc.tags || [],
        categories: doc.categories || [],
        ocr_text: doc.ocr_text || '',
        ocr_quality: doc.ocr_quality || 'basic',
        media: doc.media || {
          storage_path: '',
          thumbnail_path: doc.thumbnail_path || '',
          file_size: 0,
          file_type: 'application/pdf',
          color_mode: 'color'
        },
        preservation: doc.preservation || {
          source_archive: doc.source || 'Unknown',
          date_archived: new Date().toISOString(),
          condition: 'good'
        },
        relations: doc.relations || {
          related_articles: [],
          event_id: null,
          person_id: [],
          advertiser_id: null,
          collections: []
        },
        metadata: doc.metadata || {}
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get document history
   */
  async getDocumentHistory(id: string): Promise<DocumentHistoryResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}/history`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document history: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Upload a file to R2 storage
   */
  async uploadFile(file: File): Promise<{
    success: boolean;
    file: {
      id: string;
      filename: string;
      storage_path: string;
      thumbnail_path: string | null;
      file_type: string;
      file_size: number;
      uploaded_at: string;
    };
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'X-API-Token': API_TOKEN,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("File upload error response:", errorBody);
        throw new Error(`Failed to upload file: ${response.status} ${response.statusText} - ${errorBody}`);
      }

      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },
  
  /**
   * Activate a document
   */
  async activateDocument(id: string): Promise<Document> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}/activate`, {
        method: 'PATCH', // Or POST, depending on API design
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to activate document: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Deactivate a document
   */
  async deactivateDocument(id: string): Promise<Document> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${id}/deactivate`, {
        method: 'PATCH', // Or POST, depending on API design
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to deactivate document: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get download URL for a document file
   */
  async getFileDownloadUrl(documentId: string): Promise<{ download_url: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/download-url`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get download URL: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Remove file association from document and archive the file
   */
  async removeDocumentFile(documentId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/file`, {
        method: 'DELETE',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to remove document file: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Check if current user has permission for file operations
   */
  checkFilePermissions(action: 'remove' | 'download'): boolean {
    // For now, assume user role is stored in localStorage
    // This should be replaced with proper auth context when available
    const userRole = localStorage.getItem('user_role') || 'subscriber';
    
    switch (action) {
      case 'download':
        // All roles can download
        return ['admin', 'editor', 'contributor', 'subscriber'].includes(userRole);
      case 'remove':
        // Only admin and editor can remove files
        return ['admin', 'editor'].includes(userRole);
      default:
        return false;
    }
  }
};

export default DocumentsService;
